export type ListEntry = { text: string; image?: string };
export type SavedList = { entries: ListEntry[]; imageSize?: number; imageRadius?: number; items?: string[] };
export type ListMeta = { name: string; size: number; mtime: number };

const BASES = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
  ? ['http://localhost:8000']
  : [''];

function fetchWithTimeout(input: string, init?: RequestInit, timeoutMs: number = 15000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  const merged: RequestInit = { ...(init || {}), signal: controller.signal };
  return fetch(input, merged).finally(() => clearTimeout(id));
}

async function tryFetch(path: string, init?: RequestInit) {
  let last: any = null;
  for (const b of BASES) {
    try {
      const url = b ? `${b}${path}` : path;
      const res = await fetchWithTimeout(url, init);
      if (res.ok) return res;
      last = new Error(`HTTP ${res.status}`);
    } catch (e) { last = e; }
  }
  throw last || new Error('Network error');
}

async function parseJson(res: Response) {
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('application/json')) return await res.json();
  const text = await res.text().catch(() => '');
  const snippet = text ? text.slice(0, 200) : '';
  throw new Error(`Expected JSON but got ${ct || 'unknown content-type'}${snippet ? `: ${snippet}` : ''}`);
}

export async function listLists(): Promise<ListMeta[]> {
  const res = await tryFetch('/list_lists.php', { cache: 'no-store' });
  const data = await parseJson(res);
  return data.items || [];
}

export async function getList(name: string): Promise<SavedList | null> {
  try {
    const res = await tryFetch(`/get_list.php?name=${encodeURIComponent(name)}`, { cache: 'no-store' });
    const data = await parseJson(res);
    return (data.list as SavedList) || null;
  } catch {
    return null;
  }
}

export async function saveList(name: string, list: SavedList): Promise<boolean> {
  const res = await tryFetch('/save_list.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, list }) });
  if (!res.ok) return false;
  const data = await parseJson(res);
  return !!data.ok;
}

export async function deleteList(name: string): Promise<boolean> {
  const res = await tryFetch('/delete_list.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
  if (!res.ok) return false;
  const data = await parseJson(res);
  return !!data.ok;
}

export async function renameList(oldName: string, newName: string): Promise<boolean> {
  const res = await tryFetch('/rename_list.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ oldName, newName }) });
  if (!res.ok) return false;
  const data = await parseJson(res);
  return !!data.ok;
}
